import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import moment from 'moment';
import Icon from '../AppIcon';
import { changeLocale } from '../../api/General/account';
import constants from '../../assets/constants';
import { useToast, ToastRenderer } from '../../hooks/useToast';
import { applyDocumentLocale } from '../../utils/documentLocale';
import { getPersistor } from '../../store/redux/store/store';

interface RootState {
  user: {
    isLoggedIn: boolean;
    companyLocales?: string;
  };
}

/**
 * CO-865: the LAST language picked wins.
 *
 * i18next 22.5.1 `changeLanguage` applies each request when ITS resources finish loading
 * (node_modules/i18next/dist/cjs/i18next.js, `done` -> `setLngProps`), with no check that a newer
 * request was made in the meantime. Picking Arabic before its bundle is cached and then English
 * (already loaded) let English apply first and Arabic apply LATER: the page ended Arabic and
 * right-to-left although English was the last choice and the one saved to the account.
 *
 * Every request from the switch records its target here and gets a sequence number. When a request
 * resolves and a newer one exists whose language is not the one now applied, the newer choice is
 * re-asserted: the document direction at once, then i18next (its resources are loaded or loading, so
 * this is cheap). Module-level so every mounted switch shares it.
 */
let lastRequestedLanguage: string | null = null;
let languageRequestSeq = 0;

const changeLanguageLastWins = (i18n: I18nInstance, lang: string): void => {
  lastRequestedLanguage = lang;
  const seq = ++languageRequestSeq;
  void i18n.changeLanguage(lang)
    .then(() => {
      const wanted = lastRequestedLanguage;
      if (seq === languageRequestSeq || !wanted || i18n.language === wanted) return;
      // An older request resolved after a newer one and took the screen back.
      applyDocumentLocale(wanted);
      void i18n.changeLanguage(wanted).catch(() => { /* the newer request's own handler reports nothing either */ });
    })
    .catch(() => { /* a failed bundle load keeps the current language; nothing to undo */ });
};

/**
 * Language switch dropdown in TopBar.
 * Integrates with existing i18n system and persists selection to server.
 */
const LanguageSwitch: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  const { isLoggedIn, companyLocales } = useSelector((state: RootState) => state.user);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine allowed languages from company config or default
  const languagesAllowed = companyLocales
    ? String(companyLocales).split(',')
    : ['en', 'ar', 'fr'];

  const languageNames: Record<string, string> = {
    en: 'English',
    ar: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629',
    fr: 'Fran\u00E7ais',
  };

  const handleLanguageChange = (lang: string) => {
    setIsOpen(false);
    // CO-865: `i18n.language` lags a pending switch (it changes only once the bundle has loaded), so
    // "already on this language" is true only when no newer pick is still waiting for its bundle.
    // Otherwise Arabic-then-English returned here on the English click and Arabic won.
    const pendingOther = lastRequestedLanguage !== null && lastRequestedLanguage !== i18n.language;
    if (i18n.language === lang && !pendingOther) return;

    // Apply the switch immediately — the UI must not wait on (or be blocked by) the server
    // round-trip. A slow or failed persist previously left the picker doing nothing.
    changeLanguageLastWins(i18n, lang);
    dispatch({ type: 'SET_LOCALE', payload: { locale: lang } });
    moment.locale(lang);

    // `<html lang>` / `<html dir>`, set HERE and synchronously. App.tsx's effect also applies
    // them, but an effect runs after i18next/redux have settled and the ar → en direction was
    // measured leaving `lang="ar"` on the document. Doing it in the click handler makes the
    // document match the user's choice before anything can await.
    applyDocumentLocale(lang);

    // FLUSH THE PERSIST WRITE. `redux-persist` schedules its localStorage write on a timer, and
    // both readers of the persisted locale run at BOOT — i18n.js (`initialLng`, i18n.js:14-28)
    // and main.tsx's pre-mount `applyDocumentLocale` — off the same `persist:root` key. A reload
    // fired immediately after the click therefore re-read the OLD locale and the switch appeared
    // to be forgotten. `flush()` forces the write out now; it resolves after storage is written,
    // and a failure here is not worth a user-visible error (the switch still holds for the
    // session, which is exactly what the toast below promises).
    try {
      void getPersistor()?.flush?.()?.catch?.(() => {});
    } catch { /* persistor not available (tests / SSR) — the session switch still applied */ }

    // Handle Arabic locale with custom number format
    if (constants.rtlLangs.indexOf(lang) > -1 && lang === 'ar') {
      moment.defineLocale('ar-arNum', {
        parentLocale: 'ar',
        preparse: (str: string) => str,
        postformat: (str: string) => str,
      });
      moment.locale('ar-arNum');
    }

    // Persist to the server in the background; surface a toast if it fails but keep the
    // visible switch (it still applies for this session).
    if (isLoggedIn) {
      changeLocale(lang).catch((error) => {
        console.error('Failed to persist locale:', error);
        showToast(
          t('Could not save your language preference. It will apply for this session.'),
          'warning',
        );
      });
    }
  };

  return (
    <>
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 min-h-[44px] min-w-[44px] rounded-lg hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        title={t('Change Language') || 'Change Language'}
      >
        <Icon name="Globe" size={20} className="text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-40 bg-popover rounded-lg shadow-lg border border-border z-50">
          <div className="py-1">
            {languagesAllowed.map((lang) => (
              <button
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className={`w-full px-4 py-2 min-h-[44px] text-sm text-start hover:bg-muted transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                  i18n.language === lang ? 'bg-primary/5 text-primary font-medium' : 'text-foreground'
                }`}
              >
                {languageNames[lang] || lang}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
    <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </>
  );
};

export default LanguageSwitch;
